import { defined } from "./variable-evaluation";

export enum ERequestStatus {
  None = "None",
  Fetching = "Fetching",
  Fetched = "Fetched",
  Error = "Error",
}

export interface IRequest<RES> {
  status: ERequestStatus;
  responseData?: RES;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- populated from heterogeneous sources (RTK Query's `unknown`-typed error, parsed fetch() error bodies, action payloads); most read only `.message`/`.errors`, but narrowing this needs auditing every one of the 50+ call sites that assign into it, not a type-only change
  error?: any;
}

export interface IGetRequest<RES> {
  status: ERequestStatus;
  responseData?: RES;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see IRequest.error above
  error?: any;
  abortController?: AbortController;
}

export interface IPostRequest<REQ, RES> {
  status: ERequestStatus;
  requestData?: REQ;
  responseData?: RES;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see IRequest.error above
  error?: any;
}

export function isNone<T>(object: IGetRequest<T>): boolean {
  return object && object.status === ERequestStatus.None;
}

export function isFetching<T>(object: IGetRequest<T>): boolean {
  return object && object.status === ERequestStatus.Fetching;
}

export function isFetched<T>(object: IGetRequest<T>): boolean {
  return object && object.status === ERequestStatus.Fetched;
}

export function isError<T>(object: IGetRequest<T>): boolean {
  return object && object.status === ERequestStatus.Error;
}

export function requestInit<T>(dummyData?: T) {
  if (defined(dummyData)) {
    return {
      status: ERequestStatus.None,
      responseData: dummyData,
    };
  } else {
    return {
      status: ERequestStatus.None,
    };
  }
}

export function isContinuousArrayFetched<T>(getRequest: IGetRequest<T>) {
  return (
    defined(getRequest) &&
    defined(getRequest.responseData) &&
    !isNone(getRequest) &&
    !isError(getRequest)
  );
}

export function chainAsyncStatusFromObject<TFirst, TSecond>(
  firstAsync: IRequest<TFirst>,
  secondAsync: IRequest<TSecond>
) {
  if (!isFetched(firstAsync)) {
    return firstAsync.status;
  } else {
    return secondAsync.status;
  }
}

// TODO: improve to handle as array
export function chainAsyncStatus(
  firstAsync: ERequestStatus,
  secondAsync: ERequestStatus,
  thirdAsync?: ERequestStatus
) {
  if (firstAsync !== ERequestStatus.Fetched) {
    return firstAsync;
  }
  if (!thirdAsync) {
    return secondAsync;
  }
  if (secondAsync !== ERequestStatus.Fetched) {
    return secondAsync;
  }
  return thirdAsync;
}
